// Story generation (DESIGN PHASE) — GROUP + CATALOG driven.
// A "story" is a recognised Stat/Event row in the admin catalog. Each row has:
//   group_id   which group it belongs to (Skater Stats, Goalie Stats, ...)
//   stat       the tracked stat/event label (e.g. "G (Goals)", "Draft Day")
//   phase      "pre" | "live" | "post" | "any"
//   subject_kind  the entity it attaches to (player/team/coach/arena/referee)
//   headline   token string (revealed by H in admin)
//   descriptions  several blurb variants, rotated (revealed by B in admin)
//   priority   lower = wins a slot first
//   display    "always" or "if_space" (only when >= min_free slots remain)
//   min_free   threshold for "if_space"
// Groups carry an order + a configurable max_slots (slots per group/section).
// The Stories page renders ONE SECTION PER GROUP (in group order).
//
// Tokens: {name} {last} {team} {opp} {venue} {ord}
import { TEAM_INFO } from "./teamMap";

export const PHASES = {
    pre: { key: "pre", label: "Pregame", color: "#38BDF8" },
    live: { key: "live", label: "Live", color: "#F43F5E" },
    post: { key: "post", label: "Postgame", color: "#F5C518" },
};
export const PHASE_OPTS = [
    { key: "pre", label: "Pregame" }, { key: "live", label: "Live" }, { key: "post", label: "Postgame" },
    { key: "date", label: "Date-based" }, { key: "long", label: "Long-term" }, { key: "any", label: "Any" },
];
export const SUBJECT_KINDS = ["player", "team", "coach", "arena", "referee", "game", "league"];
export const DISPLAY_OPTS = [{ key: "always", label: "Always show" }, { key: "if_space", label: "Show if space" }];

// Recognised stat / event codes the Code column picks from (grouped). The big
// catalog table will expand/refine these; "Custom…" still allows a one-off code.
export const STAT_OPTS = [
    { group: "Skater", items: [["G", "Goals"], ["A", "Assists"], ["P", "Points"], ["+/-", "Plus/Minus"], ["PIM", "Penalty Minutes"], ["SOG", "Shots on Goal"], ["PPG", "Power-Play Goals"], ["SHG", "Short-Handed Goals"], ["GWG", "Game-Winning Goals"], ["FO%", "Faceoff %"], ["HIT", "Hits"], ["BLK", "Blocks"], ["TOI", "Time on Ice"]] },
    { group: "Goalie", items: [["W", "Wins"], ["SV", "Saves"], ["SV%", "Save %"], ["GAA", "Goals-Against Avg"], ["SHO", "Shutouts"], ["GA", "Goals Against"]] },
    { group: "Team", items: [["GF", "Goals For"], ["GA", "Goals Against"], ["PP%", "Power Play %"], ["PK%", "Penalty Kill %"], ["PTS", "Points"], ["DIFF", "Goal Differential"], ["STRK", "Streak"]] },
    { group: "Event", items: [["OT", "Overtime"], ["SO", "Shootout"], ["DRAFT", "Draft"], ["TRADE", "Trade"], ["SIGN", "Signing"], ["NEWS", "News"], ["VID", "Video"]] },
];
export const STAT_CODE_SET = new Set(STAT_OPTS.flatMap((g) => g.items.map((i) => i[0])));
export const STAT_LABEL = Object.fromEntries(STAT_OPTS.flatMap((g) => g.items.map(([c, l]) => [c, l])));

// ---- DEFAULT groups + catalog ----
export const DEFAULT_GROUPS = [
    { id: "skater", name: "Skater Stats", order: 0, max_slots: 5 },
    { id: "goalie", name: "Goalie Stats", order: 1, max_slots: 3 },
    { id: "team", name: "Team Stats", order: 2, max_slots: 3 },
    { id: "milestone", name: "Milestones", order: 3, max_slots: 3 },
    { id: "events", name: "Events", order: 4, max_slots: 3 },
];
export const DEFAULT_STORIES = [
    // Skater stats
    { id: "sk-streak", group_id: "skater", stat: "Point Streak", phase: "pre", subject_kind: "player", priority: 1, display: "always", min_free: 1,
        headline: "Can {last} extend the streak?", descriptions: ["{name} carries a hot run into the clash with {opp}. A point tonight makes it five straight.", "{name} has been unstoppable lately — {opp} will be desperate to cool the run.", "Every shift counts for {name}, who looks to keep the streak alive against {opp}."] },
    { id: "sk-gwg", group_id: "skater", stat: "G (Game-Winner)", phase: "post", subject_kind: "player", priority: 2, display: "always", min_free: 1,
        headline: "{last} seals it with the winner", descriptions: ["{name} delivers the game-winning goal as {team} edge {opp}.", "Ice in her veins — {name} buries the winner against {opp}.", "{name}'s late strike is the difference as {team} hold off {opp}."] },
    { id: "sk-first", group_id: "skater", stat: "First Goal", phase: "live", subject_kind: "player", priority: 3, display: "if_space", min_free: 2,
        headline: "{last} opens the scoring", descriptions: ["{name} buries the first of the night to set the tone early.", "{name} strikes first and {team} have the lead at {venue}.", "An early marker from {name} swings momentum {team}'s way."] },
    { id: "sk-multi", group_id: "skater", stat: "Multi-Point Night", phase: "post", subject_kind: "player", priority: 4, display: "if_space", min_free: 3,
        headline: "Three-point night for {last}", descriptions: ["{name} factors on three as {team} roll past {opp}.", "A multi-point masterclass from {name} powers {team}.", "{name} can't be stopped — three points in the {team} win."] },
    // Goalie stats
    { id: "go-so", group_id: "goalie", stat: "Shutout (SO)", phase: "post", subject_kind: "player", priority: 1, display: "always", min_free: 1,
        headline: "{last} stars in the shutout", descriptions: ["A clean sheet and a standout night caps it for {name}.", "{name} slams the door — a shutout to remember at {venue}.", "Perfection in the crease: {name} blanks {opp}."] },
    { id: "go-duel", group_id: "goalie", stat: "Save % Duel", phase: "live", subject_kind: "player", priority: 2, display: "if_space", min_free: 2,
        headline: "Goalie duel in {venue}", descriptions: ["Both nets standing tall — {team} and {opp} locked in a low-event battle.", "Saves at both ends as {team} and {opp} grind out a tight one.", "A goaltending clinic at {venue} keeps this one scoreless and tense."] },
    // Team stats
    { id: "tm-matchup", group_id: "team", stat: "Matchup", phase: "pre", subject_kind: "team", priority: 1, display: "always", min_free: 1,
        headline: "{team} vs {opp}: the rematch", descriptions: ["{name} meet {opp} again with seeding on the line.", "{name} and {opp} square off in a game with playoff implications.", "A familiar foe at {venue} — {name} look to settle the score with {opp}."] },
    { id: "tm-pp", group_id: "team", stat: "Team PP%", phase: "pre", subject_kind: "team", priority: 2, display: "if_space", min_free: 2,
        headline: "{team}'s power play heating up", descriptions: ["{name}'s power play is clicking at the right time ahead of {opp}.", "Special teams could decide it — {name} ride a hot man-advantage into {opp}.", "{name} look to keep the power play rolling against {opp}."] },
    { id: "tm-comeback", group_id: "team", stat: "Comeback", phase: "post", subject_kind: "team", priority: 3, display: "always", min_free: 1,
        headline: "{team} complete the comeback", descriptions: ["{name} storm back to stun {opp} late.", "{name} dig deep and overturn the deficit on {opp}.", "A third-period surge lifts {name} past {opp}."] },
    // Milestones
    { id: "ms-player", group_id: "milestone", stat: "Career Milestone", phase: "pre", subject_kind: "player", priority: 1, display: "always", min_free: 1,
        headline: "{last} chases a milestone", descriptions: ["{name} sits one goal shy of a career marker. The countdown is on.", "A statement night could see {name} reach a personal milestone against {opp}.", "History is in reach for {name}, inching toward a career number tonight."] },
    { id: "ms-arena", group_id: "milestone", stat: "Arena Milestone", phase: "any", subject_kind: "arena", priority: 2, display: "if_space", min_free: 1,
        headline: "{name} hits a milestone", descriptions: ["{name} hosts its {ord} PWHL game — a building steeped in big nights.", "{ord} and counting: {name} marks another milestone night.", "{name} adds to its history with the {ord} PWHL game on its ice."] },
    { id: "ms-ref", group_id: "milestone", stat: "Official Milestone", phase: "any", subject_kind: "referee", priority: 3, display: "if_space", min_free: 1,
        headline: "{name} works a milestone game", descriptions: ["Official {name} reaches their {ord} PWHL assignment.", "{ord} assignment for {name} — a steady hand on the ice.", "{name} hits a career milestone with the {ord} PWHL game."] },
    // Events
    { id: "ev-draft", group_id: "events", stat: "Draft Day", phase: "any", subject_kind: "team", priority: 1, display: "always", min_free: 1,
        headline: "Draft day arrives for {team}", descriptions: ["{name} prep their board ahead of a pivotal draft.", "Big decisions loom for {name} on draft day.", "{name} eye a franchise-shaping pick."] },
    { id: "ev-coach", group_id: "events", stat: "Coaching Move", phase: "pre", subject_kind: "coach", priority: 2, display: "if_space", min_free: 2,
        headline: "{last} shuffles the lines", descriptions: ["{name} tweaks combinations looking for a spark.", "{name} reshuffles the deck ahead of {opp}.", "New looks from {name} as the staff hunt for chemistry."] },
];
export const DEFAULT_CATALOG = { groups: DEFAULT_GROUPS, stories: DEFAULT_STORIES };

const SAMPLE_COACH_NAMES = ["Troy Ryan", "Carla MacLeod", "Kori Cheverie", "Ken Klee", "Courtney Kessel", "Greg Fargo", "Brian Idalski", "Charlie Burggraf"];
const SAMPLE_REFS = ["Elizabeth Mantha", "Kelly Cooke", "Cianna Lieffers", "Katie Johnson"];

const lastName = (s = "") => s.trim().split(/\s+/).slice(-1)[0] || s;
const abbr = (id) => TEAM_INFO[id]?.abbr || (id || "").toUpperCase();
const teamColor = (id) => TEAM_INFO[id]?.color || "#9D4CDD";
const ordinal = (n) => { const v = n % 100; if (v >= 11 && v <= 13) return `${n}th`; const r = n % 10; return `${n}${r === 1 ? "st" : r === 2 ? "nd" : r === 3 ? "rd" : "th"}`; };
const parseLocal = (s) => new Date(s + "T00:00:00");
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const toMonday = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
export const weekKeyOf = (dateStr) => ymd(toMonday(parseLocal(dateStr)));
export const weekRangeLabel = (key) => {
    const m = parseLocal(key); const s = addDays(m, 6);
    const SM = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const base = m.getMonth() === s.getMonth() ? `${SM[m.getMonth()]} ${m.getDate()} – ${s.getDate()}` : `${SM[m.getMonth()]} ${m.getDate()} – ${SM[s.getMonth()]} ${s.getDate()}`;
    return `${base}, ${m.getFullYear()}`;
};

const applyTokens = (str, ctx) => String(str || "").replace(/\{(name|last|team|opp|venue|ord)\}/g, (_, k) => (ctx[k] != null ? ctx[k] : ""));

// Merge admin catalog over defaults (admin manages the full lists once touched).
export function resolveCatalog(override) {
    const o = override || {};
    const groups = (Array.isArray(o.groups) && o.groups.length) ? o.groups : DEFAULT_GROUPS;
    const stories = (Array.isArray(o.stories) && o.stories.length) ? o.stories : DEFAULT_STORIES;
    return {
        groups: [...groups].map((g, i) => ({ max_slots: 5, order: i, ...g })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        stories: [...stories],
    };
}

const pickDesc = (descriptions, idx) => {
    const arr = Array.isArray(descriptions) ? descriptions.filter(Boolean) : [];
    return arr.length ? arr[idx % arr.length] : "";
};
const resolvePhase = (phase, game) => {
    if (phase === "pre" || phase === "live" || phase === "post") return phase;
    // date / long / any → resolve to a render phase based on game state.
    if (game && game.home_score != null && game.away_score != null) return "post";
    return "pre";
};

export function buildStories(players = [], schedule = [], opts = {}) {
    const cat = resolveCatalog(opts.catalog);
    const playerPool = (players || []).filter((p) => p.photo_url).slice(0, 20);
    const games = (schedule || []).filter((g) => g.date);
    if (!playerPool.length) return { featured: null, all: [], timeline: [], weekly: {}, weekKeys: [], groups: cat.groups };

    const teamIds = Array.from(new Set(playerPool.map((p) => p.team_id).filter(Boolean)));
    const venues = Array.from(new Set(games.map((g) => g.venue).filter(Boolean))).slice(0, 6);
    const pools = {
        player: playerPool.map((p) => ({ kind: "player", id: p.id, name: p.name, photo_url: p.photo_url, team_id: p.team_id })),
        team: (teamIds.length ? teamIds : Object.keys(TEAM_INFO).slice(0, 6)).map((tid) => ({ kind: "team", id: tid, name: TEAM_INFO[tid]?.name || tid.toUpperCase(), team_id: tid })),
        coach: SAMPLE_COACH_NAMES.map((nm, i) => ({ kind: "coach", id: `coach-${i}`, name: nm, team_id: teamIds[i % Math.max(1, teamIds.length)] || "tor" })),
        arena: (venues.length ? venues : ["TD Place", "Xcel Energy Center", "Tsongas Center"]).map((v, i) => ({ kind: "arena", id: `arena-${i}`, name: v, team_id: "", teamColor: "#F5C518" })),
        referee: SAMPLE_REFS.map((nm, i) => ({ kind: "referee", id: `ref-${i}`, name: nm, team_id: "", teamColor: "#94A3B8" })),
    };

    let weekKeys = [];
    if (games.length) {
        const dates = games.map((g) => g.date).sort();
        let m = toMonday(parseLocal(dates[0])); const end = toMonday(parseLocal(dates[dates.length - 1]));
        while (m <= end) { weekKeys.push(ymd(m)); m = addDays(m, 7); }
    } else {
        let m = addDays(toMonday(new Date()), -28); for (let i = 0; i < 9; i++) { weekKeys.push(ymd(m)); m = addDays(m, 7); }
    }
    const gamesByWeek = {};
    for (const g of games) { const k = weekKeyOf(g.date); (gamesByWeek[k] = gamesByWeek[k] || []).push(g); }
    const pickOpp = (teamId, seed) => { const ids = Object.keys(TEAM_INFO).filter((x) => x !== teamId); return ids[seed % ids.length] || "tor"; };

    const byGroupRules = {};
    for (const g of cat.groups) byGroupRules[g.id] = cat.stories.filter((s) => s.group_id === g.id).sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));

    const makeInstance = (rule, wi, slotIdx) => {
        const pool = pools[rule.subject_kind] || pools.player;
        if (!pool.length) return null;
        const idx = wi * 11 + slotIdx;
        const wg = []; // resolved below by caller via closure
        return { rule, idx, pool };
    };

    const weekly = {}; const all = [];
    weekKeys.forEach((wk, wi) => {
        const wg = gamesByWeek[wk] || [];
        const arr = [];
        cat.groups.forEach((group, gi) => {
            const rules = byGroupRules[group.id] || [];
            const max = Math.max(1, group.max_slots || 5);
            let free = max;
            rules.forEach((rule, ri) => {
                if (free <= 0) return;
                if (rule.display === "if_space" && free < (rule.min_free || 1)) return;
                const pool = pools[rule.subject_kind] || pools.player;
                if (!pool.length) return;
                const idx = wi * 13 + gi * 5 + ri;
                let subject = pool[idx % pool.length];
                const g = wg.length ? wg[(wi + ri) % wg.length] : null;
                if (rule.subject_kind === "team" && g) subject = { kind: "team", id: g.home_team_id, name: TEAM_INFO[g.home_team_id]?.name || (g.home_team_id || "").toUpperCase(), team_id: g.home_team_id };
                const oppId = g ? (g.home_team_id === subject.team_id ? g.away_team_id : (g.away_team_id === subject.team_id ? g.home_team_id : (g.away_team_id || g.home_team_id))) : pickOpp(subject.team_id, idx);
                const date = g ? g.date : ymd(addDays(parseLocal(wk), ri % 5));
                const venue = (g && g.venue) || venues[idx % Math.max(1, venues.length)] || "the rink";
                const isMilestone = rule.subject_kind === "arena" || rule.subject_kind === "referee";
                const ctx = isMilestone
                    ? { name: subject.name, last: subject.name, team: "", opp: "", venue: subject.name, ord: ordinal(50 + (idx * 13) % 200) }
                    : { name: subject.name, last: rule.subject_kind === "team" ? abbr(subject.team_id) : lastName(subject.name), team: abbr(subject.team_id), opp: abbr(oppId), venue, ord: "" };
                const phase = resolvePhase(rule.phase, g);
                const story = {
                    id: `s-${rule.id}-${wk}-${ri}`, group_id: group.id, stat: rule.stat, theme: rule.stat, phase, tag: PHASES[phase].label,
                    headline: applyTokens(rule.headline, ctx), blurb: applyTokens(pickDesc(rule.descriptions, idx), ctx),
                    excitement: Math.max(40, Math.min(99, 96 - (rule.priority || 1) * 5 + ((idx * 7) % 12))),
                    date, subject, teamId: subject.team_id || "", teamColor: subject.teamColor || teamColor(subject.team_id),
                    oppId: oppId || subject.team_id || "", oppColor: teamColor(oppId), gameId: g?.id || null, weekKey: wk,
                };
                arr.push(story); all.push(story); free -= 1;
            });
        });
        weekly[wk] = arr;
    });

    const byExcite = [...all].sort((a, b) => b.excitement - a.excitement);
    const featured = byExcite.find((s) => s.subject.kind === "player") || byExcite[0] || null;
    const timeline = [...all].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : b.excitement - a.excitement)).slice(0, 80);
    return { featured, all, timeline, weekly, weekKeys, groups: cat.groups };
}

// Group a story list by group_id, ordered by the supplied groups, each capped to max_slots.
export function byGroup(list = [], groups = DEFAULT_GROUPS) {
    return [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((g) => ({
        group: g,
        stories: list.filter((s) => s.group_id === g.id).sort((a, b) => b.excitement - a.excitement).slice(0, Math.max(1, g.max_slots || 5)),
    }));
}
// Split a list into Pregame / Live / Postgame columns.
export function byPhase(list = []) {
    const g = { pre: [], live: [], post: [] };
    for (const s of list) (g[s.phase] || g.pre).push(s);
    return g;
}

export const fmtStoryDate = (iso) => {
    try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
    catch { return iso; }
};
